import { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  GET_ALL_GISTS,
  GET_ALL_SUBJECTS,
  GET_GIST_BY_PUBLIC_ID,
  GET_GISTS_BY_SUBJECT,
} from "../../../services/graphql/queriesMutations";
import { MAX_PICKER_ROOTS } from "../model/constants";
import type {
  GistItem,
  GistsQuery,
  SubjectsQuery,
} from "../model/types";

import {
  buildBuckets,
  buildGistTree,
  buildStats,
  chooseFeaturedSlots,
  compareGistsCanonically,
  countGistMetrics,
  flattenGistTree,
  getDescendantIds,
  getGistSiblingNavigation,
  getSubjectPath,
} from "../model/subjectSingleModel";

function compareGistsByActivity(a: GistItem, b: GistItem): number {
  return (countGistMetrics(b).latestActivity || b.createdAt || "").localeCompare(
    countGistMetrics(a).latestActivity || a.createdAt || "",
  );
}

function sortGistsByWorkspaceOrder(items: GistItem[]): GistItem[] {
  return items.slice().sort((a, b) =>
    compareGistsCanonically(a, b, compareGistsByActivity),
  );
}

type GistByPublicIdQuery = {
  gistByPublicId: GistItem | null;
};

type GistsBySubjectQuery = {
  gistsBySubject: GistItem[];
};

const SUBJECT_WORKSPACE_GIST_LIMIT = 1000;

interface UseSubjectWorkspaceDataArgs {
  selectedSubjectId: number | null;
  selectedSubjectPublicId: string | null;
  selectedGistPublicId: string | null;
  openedGistId: number | null;
  gistExplorerSelectedId: number | null;
  selectedGistId: number;
}

export const useSubjectWorkspaceData = ({
  selectedSubjectId,
  selectedSubjectPublicId,
  selectedGistPublicId,
  openedGistId,
  gistExplorerSelectedId,
  selectedGistId,
}: UseSubjectWorkspaceDataArgs) => {
  const {
    data: subjectData,
    loading: subjectsLoading,
    error: subjectsError,
  } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });
  const {
    data: gistData,
    loading: gistsLoading,
    error: gistsError,
    refetch: refetchGists,
  } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const subjects = useMemo(() => subjectData?.subjects || [], [subjectData]);
  const baseGists = useMemo(() => gistData?.gists || [], [gistData]);
  const buckets = useMemo(() => buildBuckets(subjects), [subjects]);
  const routeGistFromBase = useMemo(
    () =>
      selectedGistPublicId
        ? baseGists.find((gist) => gist.publicId === selectedGistPublicId) || null
        : null,
    [baseGists, selectedGistPublicId],
  );
  const {
    data: routeGistData,
    loading: routeGistLoading,
    error: routeGistError,
    refetch: refetchRouteGist,
  } = useQuery<GistByPublicIdQuery>(GET_GIST_BY_PUBLIC_ID, {
    variables: { publicId: selectedGistPublicId || "" },
    skip: !selectedGistPublicId || Boolean(routeGistFromBase),
    fetchPolicy: "cache-and-network",
  });
  const routeGist = useMemo(
    () => routeGistFromBase || routeGistData?.gistByPublicId || null,
    [routeGistData, routeGistFromBase],
  );
  const selectedSubject = useMemo(() => {
    if (typeof routeGist?.subjectId === "number") {
      return subjects.find((subject) => subject.id === routeGist.subjectId) || null;
    }
    if (selectedSubjectPublicId) {
      return subjects.find((subject) => subject.publicId === selectedSubjectPublicId) || null;
    }
    return subjects.find((subject) => subject.id === selectedSubjectId) || null;
  }, [routeGist?.subjectId, selectedSubjectId, selectedSubjectPublicId, subjects]);

  const {
    data: selectedSubjectGistData,
    loading: selectedSubjectGistsLoading,
    error: selectedSubjectGistsError,
    refetch: refetchSelectedSubjectGists,
  } = useQuery<GistsBySubjectQuery>(GET_GISTS_BY_SUBJECT, {
    variables: {
      subjectId: selectedSubject?.id ?? 0,
      limit: SUBJECT_WORKSPACE_GIST_LIMIT,
    },
    skip: !selectedSubject?.id,
    fetchPolicy: "cache-and-network",
  });
  const gists = useMemo(() => {
    const uniqueById = new Map<number, GistItem>();
    baseGists.forEach((gist) => uniqueById.set(gist.id, gist));
    if (routeGist) uniqueById.set(routeGist.id, routeGist);
    (selectedSubjectGistData?.gistsBySubject || []).forEach((gist) =>
      uniqueById.set(gist.id, gist),
    );
    return Array.from(uniqueById.values());
  }, [baseGists, routeGist, selectedSubjectGistData]);

  const subjectStats = useMemo(
    () => buildStats(subjects, gists, buckets),
    [buckets, gists, subjects],
  );
  const rootSubjects = useMemo(() => {
    const roots = buckets.root || [];
    const activeRoots = roots.filter(
      (subject) =>
        (subjectStats[subject.id]?.branchGistCount || 0) > 0 ||
        (subjectStats[subject.id]?.childCount || 0) > 0,
    );
    return (activeRoots.length ? activeRoots : roots).slice(
      0,
      MAX_PICKER_ROOTS,
    );
  }, [buckets, subjectStats]);
  const selectedPath = useMemo(
    () => getSubjectPath(subjects, selectedSubject?.id ?? null),
    [selectedSubject?.id, subjects],
  );
  const selectedDescendantIds = useMemo(
    () =>
      selectedSubject ? getDescendantIds(selectedSubject.id, buckets) : [],
    [buckets, selectedSubject],
  );
  const branchSubjectIds = useMemo(() => {
    if (!selectedSubject) return new Set<number>();
    return new Set([selectedSubject.id, ...selectedDescendantIds]);
  }, [selectedDescendantIds, selectedSubject]);
  const branchGists = useMemo(() => {
    if (!selectedSubject) return [];
    return sortGistsByWorkspaceOrder(
      gists.filter(
        (gist) =>
          typeof gist.subjectId === "number" &&
          branchSubjectIds.has(gist.subjectId),
      ),
    );
  }, [branchSubjectIds, gists, selectedSubject]);

  const directGists = useMemo(() => {
    if (!selectedSubject) return [];
    return sortGistsByWorkspaceOrder(
      gists.filter((gist) => gist.subjectId === selectedSubject.id),
    );
  }, [gists, selectedSubject]);

  const activeGists = directGists;
  const featuredSlots = useMemo(
    () => chooseFeaturedSlots(activeGists),
    [activeGists],
  );
  const gistTree = useMemo(() => buildGistTree(activeGists), [activeGists]);
  const gistTreeFlat = useMemo(() => flattenGistTree(gistTree), [gistTree]);
  const gistExplorerSelectedGist = useMemo(
    () =>
      gistTreeFlat.find((gist) => gist.id === gistExplorerSelectedId) ||
      gistTreeFlat[0] ||
      null,
    [gistExplorerSelectedId, gistTreeFlat],
  );
  const openedGist = useMemo(
    () =>
      activeGists.find((gist) => gist.id === openedGistId) ||
      featuredSlots[0]?.gist ||
      activeGists[0] ||
      null,
    [activeGists, featuredSlots, openedGistId],
  );
  const detailGist = useMemo(
    () =>
      (selectedGistPublicId
        ? gists.find((gist) => gist.publicId === selectedGistPublicId) || null
        : null) ||
      routeGist ||
      (Number.isFinite(selectedGistId)
        ? activeGists.find((gist) => gist.id === selectedGistId) || null
        : null),
    [activeGists, gists, routeGist, selectedGistId, selectedGistPublicId],
  );
  const detailGistId = detailGist?.id ?? null;
  const currentGistSiblingNavigation = useMemo(
    () => (detailGist ? getGistSiblingNavigation(detailGist, gists) : null),
    [detailGist, gists],
  );
  const selectedStats = selectedSubject
    ? subjectStats[selectedSubject.id]
    : undefined;
  const loading = subjectsLoading || gistsLoading || routeGistLoading || selectedSubjectGistsLoading;
  const error = subjectsError || gistsError || routeGistError || selectedSubjectGistsError;
  const refetchWorkspaceGists = useCallback(async () => {
    await Promise.all([
      refetchGists(),
      selectedGistPublicId && !routeGistFromBase
        ? refetchRouteGist({ publicId: selectedGistPublicId })
        : Promise.resolve(),
      selectedSubject?.id
        ? refetchSelectedSubjectGists({
            subjectId: selectedSubject.id,
            limit: SUBJECT_WORKSPACE_GIST_LIMIT,
          })
        : Promise.resolve(),
    ]);
  }, [
    refetchGists,
    refetchRouteGist,
    refetchSelectedSubjectGists,
    routeGistFromBase,
    selectedGistPublicId,
    selectedSubject?.id,
  ]);

  return {
    subjects,
    gists,
    buckets,
    subjectStats,
    rootSubjects,
    selectedSubject,
    selectedPath,
    selectedDescendantIds,
    branchSubjectIds,
    branchGists,
    directGists,
    activeGists,
    featuredSlots,
    gistTree,
    gistTreeFlat,
    routeGist,
    gistExplorerSelectedGist,
    openedGist,
    detailGist,
    detailGistId,
    currentGistSiblingNavigation,
    selectedStats,
    loading,
    error,
    refetchGists: refetchWorkspaceGists,
  };
};
